/**
 * A bundle import on the page (decision 7, L10, L11; part 24c log, tables L and S, principles 1, 6 and 8): the bundle
 * checked whole in part 17's sandbox (part 24b's first run), its review (what it holds, and whether its book is already
 * here), and on the writer's word its second run, each batch planned (`BundleRestore`) and created under the hidden book
 * before the next is read, the found pictures through part 16a's pipeline, and the book made whole in one last write. A
 * cancel, a refusal or a failed write abandons what was created; nothing is left behind.
 *
 * Part 24d (L11's update, table S′): for a book already here, Update this book stages the bundle the same way under a
 * hidden staging book (`BundleUpdate`), then computes the update's review; the writer reviews it when they ask, and Apply
 * hands everything to the book in one write (`Workspace.applyUpdate`). A cancel, a dismissal or a failure abandons the
 * staging book; the book is untouched until Apply.
 *
 * The sandbox, the workspace's import, the picture pipeline, IDs, the clock and SHA-256 are injected, so the logic runs in
 * tests.
 */
import { imageMarkup } from '@cockatrice/core';
import { isPosition } from '@cockatrice/core';
import type { MergedChapter } from '@cockatrice/core';
import { BundleRestore, BundleUpdate } from '@cockatrice/export/bundle';
import type { BundleCheck, BundlePiece, PlannedBatch, RestoreMode } from '@cockatrice/export/bundle';
import { BOOK_IMAGE_CAP, imageRecords } from '@cockatrice/storage';
import type { BundleImport, ChapterText, EncodedImage, ImageAddOutcome, ImportWriteResult, UpdateRequest } from '@cockatrice/storage';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { ImportResult } from '../images/importer.ts';
import type { BundleReadOutcome, BundleReadRequest, ImportOutcome, ImportProblem, ImportRequest } from '../import/host.ts';
import { nameTitle } from '../import/kind.ts';
import type { SourcesChapter } from '../docx/protocol.ts';
import type { ImportStart, UpdateCommitResult } from '../shell/workspace.ts';
import { bundleBook, bundleTitle } from './book.ts';
import { chapterHeld, holdingsOf, planUpdate, updateRequest } from './update.ts';
import type { StoreRead, UpdateReview } from './update.ts';

/**
 * Why a bundle import ended without a book: the sandbox's or the bundle's problem (part 17, 24b), something of the book
 * already here when storing began (L3, `exists`) or a key held (L4, `taken`), the workspace or the lock (`not-editable`,
 * `stale`), the store (`write-failed`, `read-failed`); an update's book no longer here (part 24d, S′1, `gone`).
 */
export type BundleImportFailure = Exclude<ImportProblem, 'cancelled'> | 'exists' | 'taken' | 'not-editable' | 'stale' | 'write-failed' | 'read-failed' | 'gone';

/** Part 24d, S′6: why a ready update is ready again: the book changed since its review, or Apply couldn't begin. */
export type UpdateNote = 'changed' | 'save-failed' | 'not-editable';

/** What the review shows (table L). */
export interface BundleReview {
  readonly fileName: string;
  /** The book's title unless the writer changes it. */
  readonly title: string;
  readonly check: BundleCheck;
  /**
   * Table L: null for a restore with its IDs (L1); else why it is imported as a copy: something of the book is here (L2),
   * came here between the review and the import (L3), or some of its records' keys are held (L4; deep-fix r3).
   */
  readonly copy: 'here' | 'meanwhile' | 'clash' | null;
}

/** What the bundle import shows (table S's outcomes). */
export type BundleImportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'checking'; readonly fileName: string; readonly done: number; readonly total: number }
  | { readonly kind: 'review'; readonly review: BundleReview }
  /** S2, S′2: storing a new book or a copy, or staging an update (`update`). */
  | { readonly kind: 'storing'; readonly done: number; readonly total: number; readonly update: boolean }
  /** S3: the last write, which a cancel no longer stops. */
  | { readonly kind: 'finishing' }
  /** Part 24d, S′3: the staged update's review being computed. */
  | { readonly kind: 'planning' }
  /** Part 24d, S′3, S′6: the update staged and its review ready, with why it is ready again (null: the first time). */
  | { readonly kind: 'ready'; readonly review: UpdateReview; readonly note: UpdateNote | null }
  /** Part 24d, S′5: Apply's write, which a cancel no longer stops. */
  | { readonly kind: 'applying' }
  /** Part 24d, S′5: the update written. */
  | {
      readonly kind: 'updated';
      readonly bookId: string;
      readonly title: string;
      /** Chapters whose text changed, and of them those whose text here was kept as a copy. */
      readonly changed: number;
      readonly copies: number;
      readonly added: number;
      readonly missing: number;
      readonly recovered: number;
    }
  | { readonly kind: 'undoing' }
  /** G2 (deep-fix r5): a store the writer cancelled, undone; said, until dismissed. */
  | { readonly kind: 'cancelled' }
  | {
      readonly kind: 'done';
      /** The book made whole, which Open the book opens (S3c). */
      readonly bookId: string;
      readonly title: string;
      readonly chapters: number;
      readonly copy: boolean;
      /** Pictures with no bytes, past the cap, or found and not taken by the pipeline. */
      readonly missing: number;
      /** Refused records whose words came back (P11–P19). */
      readonly recovered: number;
    }
  /** `copy`: the review to import it as a copy instead (L3, L4), with what it needs. */
  | { readonly kind: 'failed'; readonly reason: BundleImportFailure; readonly retry: BundleReview | null };

/** What the controller needs; everything with an effect is injected. */
export interface BundleImportOptions {
  /** Part 17's sandbox, first run (`ImportSandbox.run` with format `bundle`). */
  readonly run: (file: Blob, format: 'bundle', request: ImportRequest) => Promise<ImportOutcome>;
  /** Part 24b's second run (`ImportSandbox.read`). */
  readonly read: (file: Blob, manifest: string, request: BundleReadRequest) => Promise<BundleReadOutcome>;
  /** Table HB (`Workspace.bookHere`). */
  readonly bookHere: (bookId: string) => Promise<boolean | null>;
  /** S1 (`Workspace.beginImport`). */
  readonly begin: (bookId: string) => Promise<ImportStart>;
  /** S3a (`Workspace.listImported`): the book made whole listed, nothing else changed. */
  readonly listImported: () => Promise<void>;
  /** Part 16a's pipeline for one found picture's bytes (`importImage`), its add given. */
  readonly encodePicture: (bytes: Uint8Array, add: (image: EncodedImage) => Promise<ImageAddOutcome>) => Promise<ImportResult>;
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
  readonly newId: () => string;
  /** Milliseconds since the Unix epoch. */
  readonly now: () => number;
  /** Part 24d, B′: the time zone offset at a time, in minutes east of UTC (`-new Date(at).getTimezoneOffset()`). */
  readonly zone: (at: number) => number;
  /** P6, P7: the most bytes the book's pictures may hold; `BOOK_IMAGE_CAP` unless a test sets another. */
  readonly cap?: number;
  /** Part 24d: one short read of the store in the workspace's queue (`Workspace.readForUpdate`). */
  readonly readForUpdate: StoreRead;
  /** Part 24d, U5: the review's merges, in a DOCX worker (`runMerge` with a `merge-sources` job), ended by `signal`. */
  readonly mergeSources: (chapters: readonly SourcesChapter[], signal: AbortSignal) => Promise<readonly MergedChapter[] | null>;
  /** Part 24d, S′5: Apply (`Workspace.applyUpdate`). */
  readonly applyUpdate: (running: BundleImport, request: UpdateRequest, handover: readonly ChapterText[]) => Promise<UpdateCommitResult>;
  /** The interface's words: a recovered chapter's and the pictures' chapter's titles, a picture's alt, a new book's title. */
  readonly words: {
    readonly recoveredText: () => string;
    readonly recoveredPictures: () => string;
    readonly picture: (n: number) => string;
    readonly untitled: () => string;
  };
}

/** S5, S6: an import write that wasn't done, as a failure. */
function writeFailure(result: Exclude<ImportWriteResult, { readonly kind: 'done' }>): BundleImportFailure {
  return result.kind === 'failed' ? 'write-failed' : result.kind;
}

/** Thrown out of a batch to end the second run (24b's HR6); the controller keeps why. */
class Stopped extends Error {
  override readonly name = 'Stopped';
}

/** What a second run plans each batch with: 24c's restore, or 24d's update (which is one). */
interface Planner {
  plan(pieces: readonly BundlePiece[]): Promise<PlannedBatch>;
  reserve(size: number): boolean;
}

/** How a second run ended: every piece read, the writer's cancel, or a failure; with the found pictures it added. */
interface Staged {
  readonly ended: 'read' | 'cancelled' | BundleImportFailure;
  /** P7, P23: each found picture's markup, for the recovered pictures' chapter. */
  readonly pictures: readonly string[];
  /** Found pictures the pipeline didn't take. */
  readonly lost: number;
}

/** Part 24d: an update staged and its review, until Apply or its abandon. */
interface Pending {
  /** The book updated. */
  readonly bookId: string;
  readonly running: BundleImport;
  readonly planner: BundleUpdate;
  readonly staged: Staged;
}

/** The bundle import's state and its one run. */
export class BundleImportController {
  readonly state: Accessor<BundleImportState>;
  readonly #setState: (state: BundleImportState) => void;
  readonly #options: BundleImportOptions;
  /** The run under way: a new number for each, so what an earlier one gives is ignored. */
  #job = 0;
  #abort: AbortController | null = null;
  /** The file under review or being stored. */
  #file: Blob | null = null;
  /** Part 24d: the update staged, from its staging's end to Apply or its abandon. */
  #pending: Pending | null = null;

  constructor(options: BundleImportOptions) {
    this.#options = options;
    const [state, setState] = createSignal<BundleImportState>({ kind: 'idle' });
    this.state = state;
    this.#setState = setState;
  }

  /**
   * Whether the bundle is being checked, stored or undone, or an update is staged (its button and the other imports' are
   * disabled then: one import of any kind at a time).
   */
  busy(): boolean {
    const kind = this.state().kind;
    return kind === 'checking' || kind === 'storing' || kind === 'finishing' || kind === 'undoing' || kind === 'planning' || kind === 'ready' || kind === 'applying';
  }

  /** The first run over `file`: its review follows, or why there is none; settles when the check has ended. */
  pick(file: File): Promise<void> {
    if (this.busy()) return Promise.resolve();
    this.#job += 1;
    const job = this.#job;
    const abort = new AbortController();
    this.#abort = abort;
    this.#file = file;
    this.#setState({ kind: 'checking', fileName: file.name, done: 0, total: file.size });
    return this.#check(job, file, abort.signal);
  }

  async #check(job: number, file: File, signal: AbortSignal): Promise<void> {
    const outcome = await this.#options.run(file, 'bundle', {
      signal,
      onProgress: (done, total) => {
        if (job === this.#job) this.#setState({ kind: 'checking', fileName: file.name, done, total });
      },
    });
    if (job !== this.#job) return;
    this.#abort = null;
    if (outcome.kind === 'refused') {
      this.#setState(outcome.problem === 'cancelled' ? { kind: 'idle' } : { kind: 'failed', reason: outcome.problem, retry: null });
      return;
    }
    // Only a bundle's first run says what a bundle holds.
    if (outcome.kind !== 'bundle-checked') {
      this.#setState({ kind: 'failed', reason: 'failed', retry: null });
      return;
    }
    const { check } = outcome;
    const here = await this.#options.bookHere(check.book);
    if (job !== this.#job) return;
    if (here === null) this.#setState({ kind: 'failed', reason: 'read-failed', retry: null });
    else this.#setState({ kind: 'review', review: { fileName: file.name, title: bundleTitle(check.metadata, nameTitle(file.name)), check, copy: here ? 'here' : null } });
  }

  /** Cancels the checking, or the storing or planning (what was stored is then undone, S4, S′7); nothing is kept. */
  cancel(): void {
    const kind = this.state().kind;
    if (kind !== 'checking' && kind !== 'storing' && kind !== 'planning') return;
    this.#abort?.abort();
    this.#abort = null;
    if (kind === 'checking') {
      this.#job += 1;
      this.#setState({ kind: 'idle' });
    } else this.#setState({ kind: 'undoing' });
  }

  /** The review put away, or an outcome dismissed: back to nothing, the file dropped. */
  dismiss(): void {
    if (this.busy()) return;
    this.#job += 1;
    this.#file = null;
    this.#setState({ kind: 'idle' });
  }

  /** The review put away without importing (Cancel, Escape, the dialog closed); nothing else is touched. */
  closeReview(): void {
    if (this.state().kind !== 'review') return;
    this.#job += 1;
    this.#file = null;
    this.#setState({ kind: 'idle' });
  }

  /** L3, L4: a failure that offers the copy back to its review: as a copy now, saying why, with the title typed. */
  reviewCopy(): void {
    const state = this.state();
    if (state.kind !== 'failed' || state.retry === null) return;
    this.#job += 1;
    this.#setState({ kind: 'review', review: state.retry });
  }

  /**
   * S1–S7: the reviewed bundle stored as a new book (L10) or a copy (L11, when its book is here) titled `title` (the
   * bundle's when empty or the same); settles when it has ended, the book whole or nothing left of it.
   */
  confirm(title: string): Promise<void> {
    const state = this.state();
    const file = this.#file;
    if (state.kind !== 'review' || file === null) return Promise.resolve();
    this.#job += 1;
    return this.#store(state.review, file, title, state.review.copy === null ? 'restore' : 'copy');
  }

  async #store(review: BundleReview, file: Blob, title: string, mode: RestoreMode): Promise<void> {
    const options = this.#options;
    const { check } = review;
    const abort = new AbortController();
    this.#abort = abort;
    this.#setState({ kind: 'storing', done: 0, total: file.size, update: false });
    const bookId = mode === 'restore' ? check.book : options.newId();
    const begun = await options.begin(bookId);
    if (begun.kind !== 'begun') {
      this.#abort = null;
      // L3: the copy is offered back.
      this.#fail(begun.kind === 'exists' || begun.kind === 'not-editable' ? begun.kind : writeFailure(begun), begun.kind === 'exists' ? { ...review, title, copy: 'meanwhile' } : null);
      return;
    }
    const running = begun.import;
    const now = options.now();
    const restore = new BundleRestore({ bookId, mode, owner: options.newId(), now, newId: options.newId, digest: options.digest, cap: options.cap ?? BOOK_IMAGE_CAP, zone: options.zone(now) });
    const staged = await this.#stage(running, restore, file, check.manifest, abort, false);
    let ended = staged.ended;
    if (ended === 'read') {
      this.#setState({ kind: 'finishing' });
      const book = bundleBook(bookId, restore.bookTexts(), title, options.words.untitled(), options.now());
      const last = restore.finish({ text: options.words.recoveredText(), pictures: options.words.recoveredPictures() }, staged.pictures);
      const committed = await running.commit(book, last);
      if (committed.kind === 'done') {
        await options.listImported();
        const counts = restore.counts();
        this.#setState({ kind: 'done', bookId, title: bookTitleOf(book), chapters: counts.chapters, copy: mode === 'copy', missing: counts.missing + staged.lost, recovered: counts.recovered });
        return;
      }
      ended = writeFailure(committed);
    }
    await this.#undo(running, ended, ended === 'taken' && mode === 'restore' ? { ...review, title, copy: 'clash' } : null);
  }

  /**
   * S2, S′2: the second run over `file`, each batch planned by `planner` and created through `running` before the next is
   * read, the found pictures through part 16a's pipeline into the same import; how it ended.
   */
  async #stage(running: BundleImport, planner: Planner, file: Blob, manifest: string, abort: AbortController, update: boolean): Promise<Staged> {
    const options = this.#options;
    const pictures: string[] = [];
    let lost = 0;
    /** The failure a batch met, which it throws to end the run (HR6). */
    const stop: { problem: BundleImportFailure | null } = { problem: null };
    /** S5, S6: an import write; one not done ends the run, its failure kept. */
    const write = async (result: Promise<ImportWriteResult>): Promise<void> => {
      const written = await result;
      if (written.kind === 'done') return;
      stop.problem = writeFailure(written);
      throw new Stopped(written.kind);
    };
    /** P7, A12: a found picture's records created in the import's turn, within the cap, under the import's book. */
    const add = async (image: EncodedImage): Promise<ImageAddOutcome> => {
      const records = await imageRecords(image, running.bookId, options.digest, options.newId);
      if (records === null) return { kind: 'refused' };
      if (!planner.reserve(records.image.size)) return { kind: 'full', used: 0 };
      await write(running.stage([{ kind: 'add-image', image: records.image, bytes: records.bytes }]));
      return { kind: 'added', reference: { id: records.image.id, sha256: records.image.sha256 } };
    };
    const take = async (pieces: readonly BundlePiece[]): Promise<void> => {
      const planned = await planner.plan(pieces);
      await write(running.stage(planned.ops));
      for (const bytes of planned.found) {
        const picture = await options.encodePicture(bytes, add);
        // A write that failed inside the pipeline's add ends the run here.
        if (stop.problem !== null) throw new Stopped(stop.problem);
        if (picture.kind === 'added' || picture.kind === 'existing') pictures.push(imageMarkup(options.words.picture(pictures.length + 1), picture.reference));
        else lost += 1;
      }
    };
    // HR7: a cancel ends the run at once; the batch in hand is waited for before anything is undone.
    let batch: Promise<void> = Promise.resolve();
    const outcome = await options.read(file, manifest, {
      signal: abort.signal,
      onBatch: (pieces) => {
        const taken = take(pieces);
        batch = taken.catch(() => undefined);
        return taken;
      },
      onProgress: (done, total) => {
        if (this.state().kind === 'storing') this.#setState({ kind: 'storing', done, total, update });
      },
    });
    await batch;
    this.#abort = null;
    // What ended the run: every piece read, the writer's cancel (whenever it came), or a failure: the one a batch kept when it
    // threw (HR6), else the run's own.
    const ended = abort.signal.aborted ? 'cancelled' : (stop.problem ?? (outcome.kind === 'read' ? 'read' : outcome.problem));
    return { ended, pictures, lost };
  }

  /**
   * S4–S7, S′7, S′8: what `running` created is undone (the marker stays for the next turn when it can't be, S9); then the
   * cancel said (G2), or the failure, with the copy offered when `retry` is given (L4).
   */
  async #undo(running: BundleImport, ended: 'cancelled' | BundleImportFailure, retry: BundleReview | null): Promise<void> {
    this.#pending = null;
    this.#setState({ kind: 'undoing' });
    await running.abandon();
    if (ended === 'cancelled') {
      this.#file = null;
      this.#setState({ kind: 'cancelled' });
      return;
    }
    this.#fail(ended, retry);
  }

  /**
   * Part 24d, S′1–S′3: the reviewed bundle staged as an update of its book, which is here (L2); settles when the update's
   * review is ready, or nothing is left of the staging.
   */
  confirmUpdate(): Promise<void> {
    const state = this.state();
    const file = this.#file;
    if (state.kind !== 'review' || state.review.copy !== 'here' || file === null) return Promise.resolve();
    this.#job += 1;
    return this.#update(state.review.check, file);
  }

  async #update(check: BundleCheck, file: Blob): Promise<void> {
    const options = this.#options;
    const abort = new AbortController();
    this.#abort = abort;
    this.#setState({ kind: 'storing', done: 0, total: file.size, update: true });
    // Principle 2: a hidden staging book of a fresh ID, behind 24c's marker.
    const begun = await options.begin(options.newId());
    if (begun.kind !== 'begun') {
      this.#abort = null;
      this.#fail(begun.kind === 'not-editable' ? begun.kind : begun.kind === 'exists' ? 'write-failed' : writeFailure(begun), null);
      return;
    }
    const running = begun.import;
    // S′1: what the book holds; a read that fails is `read-failed`, a book no longer here `gone`.
    const read = await options.readForUpdate(async (store) => ({ target: await holdingsOf(store, check.book) }));
    const target = read === null ? 'read-failed' : (read.target ?? 'gone');
    if (typeof target === 'string') {
      this.#abort = null;
      await this.#undo(running, target, null);
      return;
    }
    const planner = new BundleUpdate({
      bookId: check.book,
      staging: running.bookId,
      owner: options.newId(),
      now: options.now(),
      newId: options.newId,
      digest: options.digest,
      cap: options.cap ?? BOOK_IMAGE_CAP,
      zone: options.zone(options.now()),
      holdings: target.holdings,
      // N3: a read that fails counts the ID as held, so the chapter takes a fresh one.
      chapterHeld: async (id) => (await options.readForUpdate((store) => chapterHeld(store, id))) ?? true,
    });
    const staged = await this.#stage(running, planner, file, check.manifest, abort, true);
    if (staged.ended !== 'read') {
      await this.#undo(running, staged.ended, null);
      return;
    }
    const pending: Pending = { bookId: check.book, running, planner, staged };
    this.#pending = pending;
    await this.#plan(pending, null);
  }

  /** Part 24d, S′3, S′6: `pending`'s review computed from the store now; ready, with `note`, or abandoned. */
  async #plan(pending: Pending, note: UpdateNote | null): Promise<void> {
    const options = this.#options;
    const abort = new AbortController();
    this.#abort = abort;
    this.#setState({ kind: 'planning' });
    const { planner } = pending;
    const review = await planUpdate(options.readForUpdate, options.digest, (chapters) => options.mergeSources(chapters, abort.signal), {
      bookId: pending.bookId,
      matched: planner.matched(),
      added: planner.added(),
      noteOffers: planner.noteOffers(),
      shareMerges: planner.shareMerges(),
      bookTexts: planner.bookTexts(),
      counts: planner.counts(),
      staging: planner.stagingIds(),
    });
    this.#abort = null;
    if (abort.signal.aborted) {
      await this.#undo(pending.running, 'cancelled', null);
      return;
    }
    if (review === null || review === 'failed') {
      await this.#undo(pending.running, review ?? 'gone', null);
      return;
    }
    this.#setState({ kind: 'ready', review, note });
  }

  /**
   * Part 24d, S′5, S′6: the ready update applied with the offers `ticked` (by key) taken; settles when it has ended: written,
   * ready again with its review computed anew (the book changed since, or Apply couldn't begin), or abandoned.
   */
  async apply(ticked: ReadonlySet<string>): Promise<void> {
    const state = this.state();
    const pending = this.#pending;
    if (state.kind !== 'ready' || pending === null) return;
    const options = this.#options;
    const { review } = state;
    const { planner, staged, running } = pending;
    this.#setState({ kind: 'applying' });
    // N12: the recovered chapters after the book's last live chapter with a valid position (decision 62's order).
    const after = [...review.entries.values()].reduce<string | null>((last, entry) => (entry.trashed === null && isPosition(entry.position) && (last === null || entry.position > last) ? entry.position : last), null);
    // B′ (deep-fix r1): the bundle's texts Apply doesn't write whole, into each chapter's history, under its staging ID; G′3
    // (deep-fix r2): a chapter gone here, as a copy of it.
    const kept = review.chapters.flatMap(({ id, kept: text }) => {
      if (text === null) return [];
      return text.as === 'history' ? planner.keepText(text.staging, text.text) : planner.keepCopy(id, text.text);
    });
    const ops = [...planner.finish({ text: options.words.recoveredText(), pictures: options.words.recoveredPictures() }, staged.pictures, after), ...kept];
    const request = updateRequest(review, ticked, ops);
    // H′3 (deep-fix r3): the chapters whose text after Apply is the bundle's, pinned as handed over once Apply is written.
    const handover = review.chapters.flatMap((chapter) => (chapter.outcome === 'unchanged' || chapter.outcome === 'taken' || chapter.outcome === 'copied' ? [{ id: chapter.id, bookId: review.bookId, text: chapter.after }] : []));
    const result = await options.applyUpdate(running, request, handover);
    switch (result.kind) {
      case 'updated': {
        this.#pending = null;
        this.#file = null;
        const counts = planner.counts();
        this.#setState({
          kind: 'updated',
          bookId: review.bookId,
          title: review.title,
          changed: request.texts.length,
          copies: request.texts.filter((text) => text.copy).length,
          added: review.added.length,
          missing: counts.missing + staged.lost,
          recovered: counts.recovered,
        });
        return;
      }
      case 'changed':
      case 'save-failed':
      case 'not-editable':
        // S′6: nothing written; the review computed again from the store as it is now.
        await this.#plan(pending, result.kind);
        return;
      case 'taken':
      case 'stale':
      case 'failed':
        await this.#undo(running, writeFailure(result), null);
    }
  }

  /** Part 24d, S′7: the ready update put away (the review's Cancel or Escape, Dismiss on the status): its staging abandoned. */
  async discard(): Promise<void> {
    const pending = this.#pending;
    if (this.state().kind !== 'ready' || pending === null) return;
    await this.#undo(pending.running, 'cancelled', null);
  }
  #fail(reason: BundleImportFailure, retry: BundleReview | null): void {
    if (retry === null) this.#file = null;
    this.#setState({ kind: 'failed', reason, retry });
  }
}

/** The title a stored book record's metadata gives (it was made by part 05's writer or read by its reader). */
function bookTitleOf(book: { readonly metadata: string }): string {
  return bundleTitle(book.metadata, '');
}
